<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { currentUser } from '@/lib/currentUser'
import TeacherBioAchievementsTab from '@/ui-kit/teacher/bio/Achievements.vue'
import TeacherBioGeneralTab from '@/ui-kit/teacher/bio/General.vue'
import TeacherBioHeader from '@/ui-kit/teacher/bio/Header.vue'
import TeacherBioNoPurchaseCard from '@/ui-kit/teacher/bio/NoPurchaseCard.vue'
import TeacherBioReviewsTab from '@/ui-kit/teacher/bio/Reviews.vue'
import TeacherBioTariffsTab from '@/ui-kit/teacher/bio/Tariffs.vue'

interface BioContext {
  general: {
    id: number
    hmac: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface PageData {
  context: BioContext
  entry_points: Array<{ title: string; cardLink: string }> | null
}

const pageData = usePageContext<PageData>()
const ctx = pageData.context
const entryPoints = pageData.entry_points

const { isMobile } = useScreen()

interface BioUser {
  id: number
  hasTeacherPurchase: boolean
}

const tab = ref(0)
const tabs = [
  { value: 0, text: 'Основное', slug: '' },
  { value: 1, text: 'Экспертность', slug: 'seminars' },
  { value: 2, text: 'Тарифы', slug: 'tariffs' },
  { value: 3, text: 'Отзывы', slug: 'reviews' }
]

const isCurrentUser = computed(() => {
  return ctx.general.id === currentUser.id
})

const user = computed(() => currentUser as BioUser)

function onHashChange() {
  const hash = window.location.hash.replace(/#/g, '')
  const found = tabs.find(e => e.slug === hash)

  if (found) tab.value = found.value
  else window.location.hash = ''
}

watch(tab, value => {
  window.location.hash = tabs[value].slug
})

onMounted(() => {
  onHashChange()
  window.addEventListener('hashchange', onHashChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', onHashChange)
})
</script>

<template>
  <div class="account-frame">
    <app-navigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content teacher-bio">
      <template v-if="user.id === 0 && isCurrentUser">
        <header class="anon-header">
          <h1 class="anon-header__title">Моя визитка</h1>
        </header>

        <div class="teacher-bio__anonymous-card">
          <div class="teacher-bio__anonymous-card-icon mr-2">
            <v-icon size="44">user</v-icon>
          </div>
          Здесь будет ваша визитка
        </div>
      </template>

      <template v-else>
        <TeacherBioHeader :context="ctx" :is-current-user="isCurrentUser" />

        <!-- Tab select -->
        <v-select v-if="isMobile" v-model="tab" :items="tabs" class="my-3" :disabled="!!entryPoints" />
        <v-buttons-group v-else v-model="tab" class="my-3 gray-text" mode="inactive">
          <v-button
            v-for="t in tabs"
            :key="t.value"
            class="teacher-bio__tab-btn"
            mode="primary filled block"
            :disabled="isCurrentUser && !user.hasTeacherPurchase"
          >
            {{ t.text }}
          </v-button>
        </v-buttons-group>

        <TeacherBioNoPurchaseCard v-if="isCurrentUser && !user.hasTeacherPurchase" :entry-points="entryPoints" />

        <template v-else>
          <TeacherBioGeneralTab v-if="tab === 0" :context="ctx" :is-current-user="isCurrentUser" />

          <TeacherBioAchievementsTab v-if="tab === 1" :context="ctx" :is-current-user="isCurrentUser" />

          <TeacherBioTariffsTab v-if="tab === 2" :context="ctx" :is-current-user="isCurrentUser" />

          <TeacherBioReviewsTab v-if="tab === 3" :context="ctx" :is-current-user="isCurrentUser" />
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/teacher/bio.scss' as *;
</style>
